// src/pages/Insights/Tutorials/content/Python/PythonIDESetup.js
import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonIDESetup = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Step 3: The Tools</div>
                <h1>The Ultimate Developer Setup</h1>
                <p className="hero-lead">
                    You can perform surgery with a pocket knife, but surgeons prefer a scalpel.
                    Learn to configure VS Code into a high-performance "Python Engine" that writes code for you.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <h2>The "Workbench" Analogy</h2>
                <p>
                    Writing code in Notepad or IDLE is like trying to build a car in an <strong>Empty Garage</strong>.
                    You have to manually check every bolt (syntax error), remember where every tool is (no auto-complete), and if something breaks, you have to guess why.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>VS Code is your Manufacturing Plant.</strong> It doesn't just let you type text.
                        It has conveyor belts (Formatters that clean code automatically), robotic arms (Linters that catch bugs before you run), and X-Ray specs (Debuggers that see inside variables).
                    </div></ProTip>
                <p>
                    In this lesson, we won't just "install" VS Code. We will <strong>tune</strong> it.
                    A professional setup allows you to focus on the <em>logic</em>, while the IDE handles the <em>chore</em> of typing.
                </p>

                <div className="learning-objectives">
                    <h3>Engineering Goals</h3>
                    <ul>
                        <li><strong>The Toolchain:</strong> Installing the "Big 4" extensions (Python, Pylance, Ruff, Black).</li>
                        <li><strong>Configuration:</strong> Editing <code>settings.json</code> for automation (Format on Save).</li>
                        <li><strong>Debugging:</strong> Why <code>print()</code> is for amateurs and Breakpoints are for pros.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>1. Why VS Code?</h2>
                <p>
                    According to the 2024 Stack Overflow Developer Survey, <strong>Visual Studio Code</strong> is used by over 74% of all developers worldwide. It has achieved total dominance for a reason:
                </p>
                <ul>
                    <li><strong>It's Fast:</strong> Unlike full IDEs (like PyCharm) which can take minutes to load on slow laptops, VS Code opens instantly.</li>
                    <li><strong>It's Extensible:</strong> It starts empty. You add only what you need.</li>
                    <li><strong>It's Standard:</strong> If you join a company like Google or Netflix tomorrow, chances are your teammates are using it.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>2. The Professional Extension Stack</h2>
                <p>
                    Out of the box, VS Code is just a text editor. To make it a Python IDE, we need extensions.
                    Open the Extensions View (the Tetris icon on the left) and install these specific tools.
                </p>

                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Extension</th>
                            <th>Role</th>
                            <th>Why we need it</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Python (Microsoft)</strong></td>
                            <td>The Brain</td>
                            <td>The core extension. It teaches VS Code what Python is, how to run it, and how to find environments.</td>
                        </tr>
                        <tr>
                            <td><strong>Pylance</strong></td>
                            <td>The Eyes</td>
                            <td>Provides "IntelliSense". It predicts what you want to type, shows documentation when you hover over functions, and warns you if imports are missing.</td>
                        </tr>
                        <tr>
                            <td><strong>Ruff</strong></td>
                            <td>The Strict Teacher</td>
                            <td>A modern Linter. It scans your code *while you type* and underlines bugs (like unused variables) in red. It is written in Rust and is 100x faster than old tools like Pylint.</td>
                        </tr>
                        <tr>
                            <td><strong>Black Formatter</strong></td>
                            <td>The Janitor</td>
                            <td>It formats your code. You can write messy, ugly code, hit "Save", and Black instantly rewrites it to be beautiful and PEP-8 compliant.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>3. Configuration: settings.json</h2>
                <p>
                    Extensions are useless if they aren't turned on. We need to tell VS Code to use them automatically.
                </p>
                <p>
                    Pro developers don't use the Settings GUI (scrolling through 10,000 checkboxes). We edit the <code>settings.json</code> file directly.
                </p>

                <div className="numbered-steps">
                    <div className="step">
                        <h4>1. Open Settings (JSON)</h4>
                        <p>Press <code>Ctrl+Shift+P</code> (Command Palette) and type <strong>"Open User Settings (JSON)"</strong>.</p>
                    </div>
                    <div className="step">
                        <h4>2. Paste the Config</h4>
                        <p>Add these lines to your file. This creates the "God Mode" Python setup.</p>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>settings.json</span>
                    </div>
                    <CodeBlock language="json" code={`{
  // 1. Editor Basics
  "editor.formatOnSave": true,  // The Magic: Formats every time you save
  "editor.rulers": [88],        // A vertical line at 88 chars (Black's limit)
  "files.trimTrailingWhitespace": true,

  // 2. Python Specifics
  "python.defaultInterpreterPath": "./.venv/bin/python",
  "[python]": {
    "editor.defaultFormatter": "ms-python.black-formatter" // Use Black
  },

  // 3. Linting (Ruff)
  "ruff.lint.args": ["--select=E,F,I"], // Check for Errors, Flakes, and Imports
  "ruff.organizeImports": true // Sorts your imports (A, B, C...) automatically
}`} />
                </div>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Try it out:</strong> Open a Python file, write some messy code (weird spacing, unused imports), and press <strong>Ctrl+S</strong>.
                        Watch as Black and Ruff instantly snap everything into perfect order. It feels like magic.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>4. The Debugger: Stop Printing!</h2>
                <p>
                    Beginners debug by adding <code>print("HERE")</code> and <code>print(variable)</code> on every line.
                </p>
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Print Trap:</strong> Printing is effectively "spamming yourself". It clutters your console, you have to delete it later, and it doesn't show you the <em>state</em> of the program, just one value.
                    </div></ProTip>
                <p>
                    Professionals use the <strong>Debugger</strong>. It lets you "pause" time inside your code.
                </p>

                <h3>How to use Breakpoints</h3>
                <div className="use-case-list">
                    <div className="use-case-item">
                        <h3>1. Click the Gutter</h3>
                        <p>Move your mouse to the left of the line numbers in your code. Click. A  <strong>Red Dot</strong> appears. This is a Breakpoint.</p>
                    </div>
                    <div className="use-case-item">
                        <h3>2. Press F5</h3>
                        <p>This runs your code in "Debug Mode". Python will execute normally until it hits the Red Dot.</p>
                    </div>
                    <div className="use-case-item">
                        <h3>3. Freeze Frame</h3>
                        <p>The program pauses! Use the <strong>Variables Panel</strong> on the left to see the value of <em>every single variable</em> in memory right now. No print statements needed.</p>
                    </div>
                    <div className="use-case-item">
                        <h3>4. Step Through</h3>
                        <p>Use the floating control bar to "Step Over" (run the next line). Watch how variables change value in real-time as lines execute.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>5. Speed Run: Essential Keyboard Shortcuts</h2>
                <p>
                    The difference between a junior and a senior dev is often how much they touch the mouse. Pro developers stay on the keyboard.
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Shortcut</th>
                            <th>Action</th>
                            <th>Why use it?</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>F2</code></td>
                            <td>Rename Symbol</td>
                            <td>Renames a variable/function across your <strong>entire project</strong> safely.</td>
                        </tr>
                        <tr>
                            <td><code>Ctrl + .</code> (Cmd + .)</td>
                            <td>Quick Fix</td>
                            <td>Summons the "Blue Lightbulb". Fixes imports, adds docs, and solves errors instantly.</td>
                        </tr>
                        <tr>
                            <td><code>Ctrl + P</code> (Cmd + P)</td>
                            <td>Quick Open</td>
                            <td>Finds any file by name. Stop clicking through the file tree!</td>
                        </tr>
                        <tr>
                            <td><code>Ctrl + Space</code></td>
                            <td>Trigger Suggest</td>
                            <td>Forces IntelliSense to show you auto-complete options.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>6. Aesthetics: Make it Beautiful</h2>
                <p>
                    You will stare at this screen for 8 hours a day. Make it look nice. A boring gray screen causes eye strain and boredom.
                </p>
                <ul>
                    <li><strong>Theme:</strong> One Dark Pro, Dracula, or Night Owl. High contrast is better for reading code.</li>
                    <li><strong>Icons:</strong> Install <strong>Material Icon Theme</strong>. It changes the boring folder icons into colorful logos (Python snakes, React atoms, etc), making it faster to navigate your project visually.</li>
                    <li><strong>Font:</strong> Use a "Nerd Font" like <strong>Fira Code</strong> or <strong>JetBrains Mono</strong> to get ligatures (where <code>!=</code> becomes a single <code></code> symbol).</li>
                </ul>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>1. Extensions are Key</h3>
                        <p>
                            VS Code is useless without extensions. Install Python, Pylance, Ruff, and Black immediately.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>2. Format on Save</h3>
                        <p>
                            Enable <code>formatOnSave</code> in settings. It saves you dozens of hours of manual spacing adjustments per year.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>3. Use the Debugger</h3>
                        <p>
                            If you find yourself writing more than two <code>print()</code> statements to find a bug, stop. Use a Breakpoint (F5).
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>4. Ignore .vscode</h3>
                        <p>
                            Like <code>.venv</code>, the <code>.vscode</code> folder (where settings live) is usually personal preference. Add it to <code>.gitignore</code> unless your team enforces shared settings.
                        </p>
                    </div>
                </div>
            </div>

            <div className="external-resources">
                <h2> Further Reading</h2>
                <ul>
                    <li><a href="https://code.visualstudio.com/docs/python/python-tutorial" target="_blank" rel="noopener noreferrer">VS Code Official Python Tutorial</a> - The Microsoft guide.</li>
                    <li><a href="https://marketplace.visualstudio.com/items?itemName=charliermarsh.ruff" target="_blank" rel="noopener noreferrer">Ruff Extension</a> - The fastest Python linter.</li>
                    <li><a href="https://black.readthedocs.io/en/stable/" target="_blank" rel="noopener noreferrer">Black Formatter</a> - The uncompromising code formatter.</li>
                    <li><a href="https://code.visualstudio.com/docs/editor/debugging" target="_blank" rel="noopener noreferrer">Debugging in VS Code</a> - Advanced breakpoint tricks.</li>
                </ul>
            </div>
        </div>
    );
};

export default PythonIDESetup;

